import { GridRowId } from '@mui/x-data-grid/models/gridRows'
import { GridCellEditStopParams } from '@mui/x-data-grid-premium'
import {
  GridColumnVisibilityModel,
  GridRowModel,
  GridSortModel,
  MuiEvent,
} from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { TransactionListCheckboxProps } from 'src/components/FullTransactionsTable/FullTransactionsTable'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { useTransactionsColumns } from 'src/components/TransactionsDataGrid/transactionsDataGrid.columns'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import {
  getTransactionsTableFieldsCookie,
  setTransactionsTableFieldsCookie,
} from 'src/cookies'
import { OrganizationIntegrationListModel } from 'src/models/organizationIntegration.models'
import {
  TransactionDateType,
  TransactionListModel,
  TransactionSort,
} from 'src/models/transaction.models'
import { UserListModel } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import { transactionDetailUrl } from 'src/navigation/urls'
import {
  ExpenseCategoryResponse,
  getStampedLabelForExpCat,
} from 'src/queries/organizations/expenseCategories.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { usePostSingleTransactionExpenseCategory } from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { queryClient } from 'src/queryClient'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

import type {
  ExpenseCategoryCellData,
  ExpenseCategoryFieldKey,
  TransactionsDataGridRowModel,
} from './transactionsDataGrid.types'

export enum TransactionsTableLargeDesigns {
  UserAndCardAndStatus = 'userAndCardAndStatus',
  UserAndCard = 'userAndCard',
  Card = 'card',
  Base = 'base',
}

type TransactionsDataGridProps = {
  apiRef: MutableRefObject<GridApiPro | null>
  transactions: TransactionListModel[]
  infiniteScrollProps: any
  design: TransactionsTableLargeDesigns
  filters: TransactionsFiltersBody
  checkboxProps?: TransactionListCheckboxProps
  reportId?: string
  clientHeight: number
  setSort?: (sort: TransactionSort) => void
  defaultColumnVisibilityModel: GridColumnVisibilityModel
  rowHoverActions?: (params: GridRowModel) => React.ReactElement
  rowApprovalActions?: (params: GridRowModel) => React.ReactElement
  context?: TransactionReportType
}

const EDIT_MODE = 'cell'

/** Build the per-transaction EC cell data used by valueGetter/formatter */
const computeExpenseCategoryCellData = (
  transaction: TransactionListModel,
  expenseCategory: ExpenseCategoryResponse,
  user: UserListModel | undefined,
  activeIntegration: OrganizationIntegrationListModel | undefined,
  translate: (key: string) => string,
): ExpenseCategoryCellData => {
  const matchingExpenseCat = getStampedLabelForExpCat(
    transaction,
    expenseCategory,
  )

  const showMissingWhenEmpty =
    !!user?.hasOrgBookkeeperPermissions &&
    transaction.isCleared &&
    transaction.checkSingleMissingRequiredExpenseCategory(
      expenseCategory,
      activeIntegration?.connectedPlatform,
    )

  const isEmpty =
    transaction?.expenseDetails === undefined ||
    transaction?.expenseDetails?.length === 0

  let valueText = ''
  if (showMissingWhenEmpty) {
    valueText = translate('Missing')
  } else if (!isEmpty && matchingExpenseCat) {
    valueText = matchingExpenseCat.labelName
  }

  const appliedByAutomation =
    !!matchingExpenseCat?.ruleId || !!matchingExpenseCat?.generatedByPrediction

  return {
    valueText,
    labelIdOrFreeText:
      matchingExpenseCat?.labelId ?? matchingExpenseCat?.labelName,
    showMissingWhenEmpty,
    appliedByAutomation,
    generatedByPrediction: matchingExpenseCat?.generatedByPrediction,
    errorMessage: matchingExpenseCat?.errorMessage,
    cellType:
      expenseCategory.freeTextAllowed && !expenseCategory.hasLabels
        ? 'text'
        : 'select',
  }
}

const canEditExpenseCategory = (params: {
  transaction: TransactionListModel
  activeIntegration: OrganizationIntegrationListModel | undefined
}): boolean => {
  const syncStatus = params.transaction.connectedPlatforms?.find(
    (connection) =>
      connection.platform === params.activeIntegration?.connectedPlatform,
  )?.syncStatus

  return (
    syncStatus === undefined ||
    syncStatus === 'FAILED' ||
    syncStatus === 'NOT_SYNCED'
  )
}

const TransactionsDataGrid: React.FC<TransactionsDataGridProps> = (props) => {
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { data: user } = useQueryOwnUser({})
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  // ---- Sorting bridge to server sort enum
  const [sortModel, setSortModel] = React.useState<GridSortModel>([])
  useEffect(() => {
    if (sortModel?.length > 0) {
      const { field, sort } = sortModel[0] ?? {}
      const dirAsc = sort === 'asc'
      switch (field) {
        case 'merchant':
          props.setSort?.(
            dirAsc
              ? TransactionSort.merchantNameAsc
              : TransactionSort.merchantNameDesc,
          )
          break
        case 'cardUser':
          props.setSort?.(
            dirAsc
              ? TransactionSort.recipientNameAsc
              : TransactionSort.recipientNameDesc,
          )
          break
        case 'amount':
          props.setSort?.(
            dirAsc ? TransactionSort.amountAsc : TransactionSort.amountDesc,
          )
          break
        case 'date':
        default: {
          const byClear = props.filters.dateType === TransactionDateType.Clear
          props.setSort?.(
            byClear
              ? dirAsc
                ? TransactionSort.dateClearAsc
                : TransactionSort.dateClearDesc
              : dirAsc
                ? TransactionSort.dateAuthAsc
                : TransactionSort.dateAuthDesc,
          )
        }
      }
    } else {
      const byClear = props.filters.dateType === TransactionDateType.Clear
      props.setSort?.(
        byClear ? TransactionSort.dateClearDesc : TransactionSort.dateAuthDesc,
      )
    }
  }, [sortModel])

  const dispatch = useDispatch()

  const { mutateAsync: postCategoryLabel, reset: resetPostCategoryLabel } =
    usePostSingleTransactionExpenseCategory()

  const dispatchErrorToast = (e: BackendErrorPayload<string>) => {
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <BackendErrorToastMessage error={e} />,
        failureColorScheme: true,
        hideImage: true,
      }),
    )
  }

  // ---- Build columns via hook (uses Redux selectors & queries internally)
  const { columns, pinnedLeft, pinnedRight, ecLoaded, orgExpCats } =
    useTransactionsColumns({
      context: props.context,
      checkboxMinWidth: props.checkboxProps ? 115 : 150,
      filters: { dateType: props.filters?.dateType },
      rowApprovalActions: props.rowApprovalActions,
      rowHoverActions: props.rowHoverActions,
    })

  const pinned = useMemo(
    () => ({ left: pinnedLeft, right: pinnedRight }),
    [pinnedLeft, pinnedRight],
  )

  // ---- Precompute expense-category cell data for each tx (valueGetter/formatter rely on this)
  const expenseCategoryDataMemo = useMemo(() => {
    if (!orgExpCats || !user)
      return new Map<
        string,
        Record<ExpenseCategoryFieldKey, ExpenseCategoryCellData>
      >()

    const categoryDataMap = new Map<
      string,
      Record<ExpenseCategoryFieldKey, ExpenseCategoryCellData>
    >()

    props.transactions.forEach((transaction) => {
      const expenseCategoryCellData: Record<
        ExpenseCategoryFieldKey,
        ExpenseCategoryCellData
      > = {}

      orgExpCats.forEach((category) => {
        const ecKey: ExpenseCategoryFieldKey = `ec:${category.id}`
        expenseCategoryCellData[ecKey] = computeExpenseCategoryCellData(
          transaction,
          category,
          user,
          activeIntegration,
          (k) => k,
        )
      })

      categoryDataMap.set(transaction.id, expenseCategoryCellData)
    })

    return categoryDataMap
  }, [
    props.transactions,
    orgExpCats,
    user?.hasOrgBookkeeperPermissions,
    activeIntegration?.connectedPlatform,
  ])

  // ---- Rows
  const rows: TransactionsDataGridRowModel[] = useMemo(
    () =>
      props.transactions.map((transaction, i) => ({
        id: transaction.id,
        transaction,
        index: i,
        expenseCategoryCellData:
          expenseCategoryDataMemo.get(transaction.id) || {},
      })),
    [props.transactions, expenseCategoryDataMemo],
  )

  // ---- Inline EC edit commit
  const processRowUpdate = useCallback(
    async (
      newRow: TransactionsDataGridRowModel & { [key: string]: any },
      oldRow: TransactionsDataGridRowModel,
      _: { rowId: GridRowId },
    ): Promise<TransactionsDataGridRowModel> => {
      const changedEcField = Object.keys(newRow).find((k) =>
        k.startsWith('ec:'),
      )
      if (!changedEcField) return Promise.resolve(oldRow)

      const expenseCategoryId = changedEcField.substring(3)
      const value = newRow[changedEcField]
      const labelId = typeof value === 'object' ? value?.value : value
      const labelName = typeof value === 'object' ? value?.label : value

      return postCategoryLabel({
        transactionId: oldRow.transaction.id,
        categoryId: expenseCategoryId,
        body: { labelId: labelId || undefined },
      })
        .catch((e) => {
          dispatchErrorToast(e)
          return oldRow
        })
        .then(() => {
          const expCat = (orgExpCats || []).find((ec) => ec.id === expenseCategoryId)
          if (props.context === TransactionReportType.AccountingTransactions && expCat?.requiredForTransactions) {
            setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: transactionsQueryKeys.accountingStatistics(props.filters),
              })
            }, 3000)
          }
          return {
          ...newRow,
          expenseCategoryCellData: {
            ...newRow.expenseCategoryCellData,
            [changedEcField as ExpenseCategoryFieldKey]: {
              ...newRow.expenseCategoryCellData[
                changedEcField as ExpenseCategoryFieldKey
              ],
              valueText: labelName,
              labelIdOrFreeText: labelId,
            },
          },
        }})
    },
    [postCategoryLabel],
  )

  const handleCellEditStop = useCallback(
    (
      _: GridCellEditStopParams<TransactionsDataGridRowModel>,
      event: MuiEvent,
    ) => {
      resetPostCategoryLabel()
      // We render custom edit cells; prevent default MUI handlers.
      event.defaultMuiPrevented = true
    },
    [resetPostCategoryLabel],
  )

  // ---- Column visibility (cookie)
  const transactionsTableFieldsCookie = getTransactionsTableFieldsCookie()
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      date:
        transactionsTableFieldsCookie?.date ??
        props.defaultColumnVisibilityModel.date,
      merchant:
        transactionsTableFieldsCookie?.merchant ??
        props.defaultColumnVisibilityModel.merchant,
      vendor:
        transactionsTableFieldsCookie?.vendor ??
        props.defaultColumnVisibilityModel.vendor,
      card:
        transactionsTableFieldsCookie?.card ??
        props.defaultColumnVisibilityModel.card,
      cardUser:
        transactionsTableFieldsCookie?.cardUser ??
        props.defaultColumnVisibilityModel.cardUser,
      cardholderEmail:
        transactionsTableFieldsCookie?.cardholderEmail ??
        props.defaultColumnVisibilityModel.cardholderEmail,
      expenseCategories:
        transactionsTableFieldsCookie?.expenseCategories ??
        props.defaultColumnVisibilityModel.expenseCategories,
      status:
        transactionsTableFieldsCookie?.status ??
        props.defaultColumnVisibilityModel.status,
      managerReviewStatus:
        transactionsTableFieldsCookie?.managerReviewStatus ??
        props.defaultColumnVisibilityModel.managerReviewStatus,
      finalReview:
        transactionsTableFieldsCookie?.finalReview ??
        props.defaultColumnVisibilityModel.finalReview,
      amount:
        transactionsTableFieldsCookie?.amount ??
        props.defaultColumnVisibilityModel.amount,
      currency:
        transactionsTableFieldsCookie?.currency ??
        props.defaultColumnVisibilityModel.currency,
      authBillingAmountCents:
        transactionsTableFieldsCookie?.authBillingAmountCents ??
        props.defaultColumnVisibilityModel.authBillingAmountCents,
      authBillingCurrency:
        transactionsTableFieldsCookie?.authBillingCurrency ??
        props.defaultColumnVisibilityModel.authBillingCurrency,
      authMerchantAmountCents:
        transactionsTableFieldsCookie?.authMerchantAmountCents ??
        props.defaultColumnVisibilityModel.authMerchantAmountCents,
      authMerchantCurrency:
        transactionsTableFieldsCookie?.authMerchantCurrency ??
        props.defaultColumnVisibilityModel.authMerchantCurrency,
      authExchangeRate:
        transactionsTableFieldsCookie?.authExchangeRate ??
        props.defaultColumnVisibilityModel.authExchangeRate,
      attachmentsCount:
        transactionsTableFieldsCookie?.attachmentsCount ??
        props.defaultColumnVisibilityModel.attachmentsCount,
      creditCardDisplayName:
        transactionsTableFieldsCookie?.creditCardDisplayName ??
        props.defaultColumnVisibilityModel.creditCardDisplayName,
      creditCardType:
        transactionsTableFieldsCookie?.creditCardType ??
        props.defaultColumnVisibilityModel.creditCardType,
      receipts:
        transactionsTableFieldsCookie?.receipts ??
        props.defaultColumnVisibilityModel.receipts,
      txnSynced:
        transactionsTableFieldsCookie?.txnSynced ??
        props.defaultColumnVisibilityModel.txnSynced,
      receiptsSynced:
        transactionsTableFieldsCookie?.receiptsSynced ??
        props.defaultColumnVisibilityModel.receiptsSynced,
      originalMerchant:
        transactionsTableFieldsCookie?.originalMerchant ??
        props.defaultColumnVisibilityModel.originalMerchant,
    })

  // ---- Render (wait for ECs so columns/rows match)
  if (!ecLoaded) return <></>

  return (
    <DataGrid
      apiRef={props.apiRef}
      height={props.clientHeight}
      rowHeight={DataGridRowHeight.Default}
      rows={rows}
      columns={columns}
      infiniteScrollProps={props.infiniteScrollProps}
      filtersBody={props.filters}
      disableRowSelectionOnClick
      sortModel={sortModel}
      onSortModelChange={(m) => setSortModel(m)}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) => {
        setTransactionsTableFieldsCookie(newModel)
        setColumnVisibilityModel(newModel)
        analytics?.track('transactions column selector', {
          screen: calculatedBackgroundLocation.pathname?.replace?.(
            /\/cc_[a-zA-Z0-9]+/,
            '',
          ),
        })
      }}
      initialState={{ pinnedColumns: pinned }}
      alwaysVisibleColumns={['date']}
      disableColumnFilter
      checkboxSelection={props.checkboxProps !== undefined}
      onRowSelectionModelChange={props.checkboxProps?.handleCheckboxClick}
      isRowSelectable={(params): boolean => {
        const tx = params.row.transaction
        return !!props.checkboxProps?.canSelectTransaction(tx)
      }}
      onRowClick={(row): void => {
        const tx = row.row.transaction
        dispatch(
          connectedPush({
            pathname: transactionDetailUrl(tx?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        analytics?.track(
          'transactions - visit transaction with ' +
            tx.status.toLowerCase() +
            ' status - list item press',
          {
            transactionStatus: tx?.status,
            intent: 'visit transaction',
            interaction: 'list item press',
            screen: calculatedBackgroundLocation.pathname,
            transactionId: tx?.id,
          },
        )
      }}
      processRowUpdate={processRowUpdate}
      editMode={EDIT_MODE}
      onCellEditStop={handleCellEditStop}
      isCellEditable={(params) => {
        if (props.context === TransactionReportType.ExpenseReport) return false
        if (!String(params.field).startsWith('ec:')) return false
        return canEditExpenseCategory({
          transaction: params.row.transaction,
          activeIntegration,
        })
      }}
    />
  )
}

export default TransactionsDataGrid
